import { Button, Checkbox } from "antd";
import { makeAutoObservable } from "mobx";
import { observer } from "mobx-react-lite";
import * as React from "react";
import { optionsMaker } from "src/common/optionsMaker";
import { MdmDrawer } from "src/components/MdmDrawer";
import { MdmTable } from "src/components/MdmTable";
import { RtSelection } from "src/components/ReactiveTable/components/RtSelection";
import { RtColumn } from "src/components/ReactiveTable/RtColumn";
import { RtStore } from "src/components/ReactiveTable/RtStore";
import { RtKey } from "src/components/ReactiveTable/RtTypes";
import { ToolBox2 } from "src/components/ToolBox2";
import { loadRoles } from "src/pages/management/usersAndRoles/roles";
import { ZRole } from "src/types";

import { ZRoleAccess } from "../ZRoleAccess";
import * as styles from "./SelectOfAccessedRoles.module.less";

type PropsSelectOfAccessedRoles = {
  open: boolean;
  close: () => void;
  selected: ZRoleAccess[];
  onSelect(list: ZRoleAccess[]): void;
};

export const SelectOfAccessedRoles: React.FC<PropsSelectOfAccessedRoles> =
  observer((props) => {
    const { open, close, selected, onSelect } = props;
    const store = React.useMemo(() => {
      if (!open) return null;
      const disabledIds = new Set<RtKey>(selected.map(({ role }) => role.id));
      const table = new RtStore<ZRole, {}>({
        rowKey: "id",
        loader: loadRoles(),
        selectionType: "checkbox",
        rowSelectionDisabled: (row) => disabledIds.has(row.id),
      });
      const makeOption = optionsMaker([], ({ name }: ZRole) => name);
      const rowCls = (id: string) =>
        disabledIds.has(id) ? styles.disabledCell : undefined;
      const columns: RtColumn<ZRole>[] = [
        {
          key: "name",
          title: "Название",
          render: ({ id, name }) => <span className={rowCls(id)}>{name}</span>,
          width: "20%",
        },
        {
          key: "description",
          title: "Описание",
          render: ({ id, description }) => (
            <span className={rowCls(id)}>{description}</span>
          ),
        },
      ];
      return makeAutoObservable({
        table,
        makeOption,
        rights: {
          canCreate: false,
          canRead: false,
          canUpdate: false,
          canDelete: false,
        } as Omit<ZRoleAccess, "role">,
        setRight(
          key: "canCreate" | "canDelete" | "canRead" | "canUpdate",
          flag: boolean,
        ) {
          this.rights[key] = flag;
        },
        disabledIds,
        columns,
      });
    }, [open]);
    const handleSelect = () => {
      if (store) {
        onSelect?.(
          store.table.selectedKeys.map((key) => ({
            role: {
              id: String(key),
              label: store.makeOption(key).label,
            },
            ...store.rights,
          })),
        );
        close();
      }
    };
    return (
      <MdmDrawer
        title="Добавление ролей"
        open={open}
        onClose={close}
        wide
        extra={
          <Button
            type="primary"
            disabled={!store?.table.selectedKeys.length}
            onClick={handleSelect}
          >
            Выбрать
          </Button>
        }
      >
        {store && (
          <ToolBox2
            mainTool={
              <div className={styles.toolbarStack}>
                <div className={styles.crud}>
                  <span>Назначить права:</span>
                  {roleActions.map((action) => (
                    <Checkbox
                      key={action}
                      checked={!!store.rights[action]}
                      onChange={(e) => store.setRight(action, e.target.checked)}
                    >
                      {action.slice(3).toUpperCase()}
                    </Checkbox>
                  ))}
                </div>
                <RtSelection
                  store={store.table}
                  makeOption={store.makeOption}
                />
              </div>
            }
          >
            <MdmTable
              store={store.table}
              columns={store.columns}
              rowClick={{
                type: "click",
                handler: (row) => {
                  if (!store.disabledIds.has(row.id)) {
                    store.table.changeSelection(row.id, "toggle");
                  }
                },
              }}
            />
          </ToolBox2>
        )}
      </MdmDrawer>
    );
  });

export const roleActions = [
  "canCreate",
  "canRead",
  "canUpdate",
  "canDelete",
] as const;
